/* eslint-disable @next/next/no-img-element */
import {
  AnnouncementBoardIcon,
  AnnouncementSingleIcon,
  CloseIcon,
  CopyToClipboardIcon,
  FacebookNewIcon,
  LinkedIn,
  NewQuestionIcon,
  SendIcon,
  TwitterNewIcon,
  WhatsAppIcon
} from '@/src/assets/icons'
import { Box, Breadcrumbs, IconButton, TextField, Typography } from '@mui/material'
import { useRouter } from 'next/router'
import CommonApi from '../api'
import { AnnouncementSearchEndpoint } from '../api/endpoints/AnnouncementSearch'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CircularProgress } from '@mui/material'
import Link from 'next/link'
import { QuestionAnswerEndpoint } from '../api/endpoints'
import { useStore } from '@/src/types/store'
import Header from '@/src/components/announcement-header'
import { Footer } from '@/src/components/footer'
import EmptyQuestions from '@/src/components/announcements/components/EmptyQuestions'
import Button from '@/src/components/button'
import Head from 'next/head'
import CustomModal from '@/src/components/Modal'
import { TickIcon } from '../../src/assets/icons'
import {
  formatDate,
  getAnnouncementIdFromSlug,
  getWebLinkButtonText,
  getYoutubeEmbedUrl
} from '@/src/types/helper'
import TruncatedAnswer from '@/src/components/HomepageEdit/components/TruncatedElement'
import dynamic from 'next/dynamic'
import DOMPurify from 'dompurify'
import ContentTagChips from '@/src/components/content-tags/ContentTagChips'

// pdf.js is browser-only — evaluating it during SSR fails on globals Node does not expose
// to the server bundle. Every other PDF surface in the app loads the viewer this way too.
const PdfViewerMerged = dynamic(() => import('@/src/components/pdf-viewer/NewPdfViewer2'), {
  ssr: false
})

interface data {
  id: number
  headline: string | ' '
  category: string | ' '
  summary: string
  short_summary: string | null
  show_short_summary: boolean
  paragraph: string
  labels: string[]
  tags?: string[] | null
  file?: string
  like: number
  questions: number
  view: number
  is_liked: boolean
  is_followed: boolean
  toggle: boolean
  thumbnail: any
  types: string
  content_url: string
  source_name?: string
  source_icon?: string
  source_publish_at?: any
  social_description: any
  social_headline: any
  display_date?: any
  context?: string
  user_context?: string
}

export interface Question {
  result: {
    answer: string
    answer_date?: string
    answer_by?: string
    created_on: string
    id: number
    question: string
    updated_on: string
    user: string
    business_name: string
    logo_mark: string
  }[]
  private_count: number
  unanswered_count: number
  business_name?: string
}

const getTimeAgo = (dateString?: string) => {
  if (!dateString) return ''
  const diff = Date.now() - new Date(dateString).getTime()
  if (diff < 0) return 'just now'
  const seconds = Math.floor(diff / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} ago`
  const weeks = Math.floor(days / 7)
  if (weeks < 5) return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months} ${months === 1 ? 'month' : 'months'} ago`
  const years = Math.floor(days / 365)
  return `${years} ${years === 1 ? 'year' : 'years'} ago`
}

interface Props {
  req: any
  annData: any
  id: number
  fullUrl: string
  SocialMediaUrl: string
}

const AnnouncementDetail = ({ annData, fullUrl, SocialMediaUrl, id }: Props) => {
  const { profile, themeLogo, companyThumbnails, setRedirectionUrl, homeTheme, themeLoading } =
    useStore()
  const quarterlyFocusRef = useRef<HTMLInputElement | null>(null)
  const isLoggedIn = profile?.user_type ? true : false
  // TGN (Tungsten Mining) asked for the engagement sidebar (Ask management, sentiment,
  // Recent questions) to be hidden. Tenant-specific hardcoding - see TECH_DEBT.md at the
  // workspace root; remove if TGN offboards. Also hidden while the theme is still loading
  // so TGN visitors never see the sidebar flash in and out.
  const hideEngagementSidebar = themeLoading || homeTheme?.code === 'TGN'

  const [data, setData] = useState<data>(annData.respData)
  const [questionData, setQuestionData] = useState<Question | undefined>()
  const [loading, setLoading] = useState(false)
  const [showSend, setShowSend] = useState(false)
  const [shareModal, setShareModal] = useState(false)
  const [copied, setCopied] = useState(false)
  const [sendSuccess, setSendSuccess] = useState<boolean | string>(false)
  const [question, setQuestion] = useState<string>('')
  const [sentiment, setSentiment] = useState<'positive' | 'neutral' | 'negative' | ''>('')

  const socialTitle = data?.social_headline ?? data?.headline
  const socialDescription = data?.social_description ?? data?.summary
  const encodedUrl = encodeURIComponent(SocialMediaUrl ?? '')
  const encodedTitle = encodeURIComponent(socialTitle ?? '')
  const encodedDescription = encodeURIComponent(socialDescription ?? '')

  const router = useRouter()
  const handleData = async () => {
    setLoading(true)
    try {
      const response = await CommonApi({
        path: AnnouncementSearchEndpoint?.get(id),
        method: 'get',
        serverParams: `server_user_id=${profile?.id}`
      })
      setData(response.data)
    } catch (error) {}
    setLoading(false)
  }

  const handleQuestions = useCallback(async () => {
    const userId = profile?.id ?? ''

    try {
      const response = await CommonApi({
        path: QuestionAnswerEndpoint.get(id, userId),
        method: 'get'
      })
      setQuestionData(response.data)
    } catch (error) {}
  }, [profile?.id, id])

  const handleAskQuestion = () => {
    quarterlyFocusRef.current?.focus()
  }

  const handlePost = async () => {
    try {
      const response = await CommonApi({
        path: QuestionAnswerEndpoint?.post,
        method: 'post',
        data: {
          question: question,
          content_id: id
        }
      })
      setShowSend(false)
      setSendSuccess(true)
      if (response.status) {
        handleQuestions()
      }
    } catch (error) {}
    setTimeout(() => {
      setSendSuccess('sent')
    }, 2000)

    setQuestion('')
  }

  useEffect(() => {
    if (id) {
      handleQuestions()
      const stored = JSON.parse(localStorage.getItem('content_sentiments') || '{}')
      if (stored[id]) {
        setSentiment(stored[id])
      }
    }
  }, [id, handleQuestions])

  const handleChange = (event: any) => {
    const inputValue = event.target.value
    setQuestion(inputValue)

    if (inputValue.length > 0) {
      setShowSend(true)
    } else {
      setShowSend(false)
    }
  }

  const getOrCreateSessionId = () => {
    let sessionId = localStorage.getItem('sentiment_session_id')
    if (!sessionId) {
      sessionId =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`
      localStorage.setItem('sentiment_session_id', sessionId)
    }
    return sessionId
  }

  const handleSentiment = async (value: 'positive' | 'neutral' | 'negative') => {
    try {
      const sessionId = getOrCreateSessionId()
      await CommonApi({
        path: 'content/sentiment',
        method: 'patch',
        data: {
          content_id: id,
          session_id: sessionId,
          sentiment: value
        }
      })
      setSentiment(value)
      const stored = JSON.parse(localStorage.getItem('content_sentiments') || '{}')
      stored[id] = value
      localStorage.setItem('content_sentiments', JSON.stringify(stored))
    } catch (error) {
      console.log('sentiment error', error)
    }
  }

  const handleAction = async (mode: string, type: boolean, id: number) => {
    const likeData = {
      like: !type
    }
    const followData = {
      follow: !type
    }

    try {
      const response = await CommonApi({
        path: `like-view?id=${id}`,
        method: 'patch',
        data: mode === 'like' ? likeData : followData
      })
      if (response?.status) {
        handleData()
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  const truncateText = (text: string, maxLength: number) => {
    if (!text) return ''
    if (text.length <= maxLength) return text
    return text.slice(0, maxLength) + '...'
  }

  const rawUrl = data.content_url || ''

  const embedUrl = getYoutubeEmbedUrl(rawUrl)

  return (
    <>
      <Head>
        <title>{truncateText(annData?.respData?.headline, 60)}</title>
        <meta
          key="og:title"
          property="og:title"
          content={truncateText(
            annData?.respData?.social_headline || annData?.respData?.headline,
            60
          )}
        />
        <meta
          key="og:description"
          property="og:description"
          content={truncateText(
            annData?.respData?.social_description || annData?.respData?.summary,
            160
          )}
        />
        <meta
          name="description"
          content={truncateText(
            annData?.respData?.social_description || annData?.respData?.summary,
            160
          )}
        />
        <meta key="og:type" property="og:type" content="article" />
        <meta key="og:url" property="og:url" content={SocialMediaUrl} />
        <meta
          key="og:image"
          property="og:image"
          content={
            annData?.respData?.thumbnail
              ? annData?.respData?.thumbnail
              : companyThumbnails?.length > 0
                ? companyThumbnails[0]?.url
                : themeLogo?.logo
          }
        />
        <meta key="twitter:card" property="twitter:card" content="summary_large_image" />
        <meta key="twitter:url" property="twitter:url" content={SocialMediaUrl} />
        <meta
          key="twitter:title"
          property="twitter:title"
          content={truncateText(
            annData?.respData?.social_headline || annData?.respData?.headline,
            60
          )}
        />
        <meta
          key="twitter:description"
          property="twitter:description"
          content={truncateText(
            annData?.respData?.social_description || annData?.respData?.summary,
            160
          )}
        />
        <meta
          key="twitter:image"
          property="twitter:image"
          content={
            annData?.respData?.thumbnail
              ? annData?.respData?.thumbnail
              : companyThumbnails?.length > 0
                ? companyThumbnails[0]?.url
                : themeLogo?.logo
          }
        />
      </Head>

      <Header />

      <div
        className="lg:max-w-screen overflow-y-auto bg-[#f5f5f8]"
        style={{
          height: 'calc(100vh - 100px)',
          marginTop: '6rem',
          transition: 'margin-top 1s ease-in-out'
        }}
        id="annoouncementpdf"
      >
        <div className="min-h-[calc(100vh-164px)] p-6 pt-8">
          <div className="flex flex-col w-full ">
            <div className=" flex justify-center">
              <div className="flex w-full flex-col" id="scroll-up">
                {loading && (
                  <div className=" flex flex-col justify-center items-center">
                    <CircularProgress />
                  </div>
                )}
                <Breadcrumbs aria-label="breadcrumb" className="mb-6">
                  <Link
                    color="inherit"
                    href="/"
                    className="mb-1 flex items-center hover:underline decoration-secondary-main"
                  >
                    <AnnouncementSingleIcon />
                  </Link>
                  <Link
                    color="inherit"
                    href={
                      data?.category == 'announcement'
                        ? '/announcements'
                        : data?.category == 'update'
                          ? '/news-insights'
                          : data?.category == 'research'
                            ? '/research'
                            : ''
                    }
                    className="hover:underline decoration-secondary-main flex items-center"
                  >
                    <Typography variant="caption" color="text.secondary">
                      {data?.category == 'announcement'
                        ? 'Announcements'
                        : data?.category == 'research'
                          ? 'Research'
                          : 'News & Insights'}
                    </Typography>
                  </Link>
                  <div className="w-[30vw] overflow-hidden font-normal text-x flex items-center text-secondary-main87 cursor-default">
                    <Typography variant="badgeLabel" color="primary.main" className="text-ellipsis">
                      {data?.headline.replaceAll('&#47;', '/') || ' '}
                    </Typography>
                  </div>
                </Breadcrumbs>
                <div className={`flex flex-col xl:flex-row justify-between`}>
                  {!loading && (
                    <div className="grid grid-cols-1 w-full min-w-0 px-0 md:px-10 lg:flex lg:flex-col gap-4">
                      {/* // heading part  */}
                      <div className="flex flex-col gap-2 bg-white rounded-[20px] p-6">
                        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
                          <Typography
                            variant="overline"
                            color="text.secondary"
                            className="normal-case whitespace-nowrap"
                          >
                            {'Published date: ' + formatDate(data?.display_date, 'long')}
                          </Typography>
                          <div className="flex flex-wrap gap-2">
                            {data &&
                              data?.labels?.map((val: any) => (
                                <Typography
                                  variant="chipLabel"
                                  color="text.primary"
                                  key={val}
                                  bgcolor="action.selected"
                                  className="px-1.5 py-0 rounded whitespace-nowrap"
                                >
                                  {val === 'Annual Report' ? 'Company Reports' : val}
                                </Typography>
                              ))}
                          </div>
                          <ContentTagChips tags={data?.tags} />
                        </div>
                        <div className="flex flex-col gap-4">
                          <Typography
                            variant="h6"
                            color="text.primary"
                            className="break-words leading-[1.75rem]"
                            dangerouslySetInnerHTML={{
                              __html: data?.headline || ''
                            }}
                          ></Typography>
                          {/* <div className="flex items-center gap-4">
                            <div
                              onClick={() => handleAction('like', data?.is_liked, data?.id)}
                              className={`${
                                isLoggedIn ? 'cursor-pointer' : 'cursor-not-allowed'
                              } flex items-center gap-2`}
                            >
                              {data?.is_liked ? <NewLikeIconFilled /> : <NewLikeIcon />}
                              <Typography variant="caption" color="primary.main">
                                {`${data?.is_liked ? 'Liked' : 'Like'} (${data?.like})`}
                              </Typography>
                            </div>
                            <div
                              onClick={() => handleAction('follow', data?.is_followed, data?.id)}
                              className={`${
                                isLoggedIn ? 'cursor-pointer' : 'cursor-not-allowed'
                              } flex items-center gap-2`}
                            >
                              {data?.is_followed ? <BellIconFilled /> : <BellIcon />}
                              <Typography variant="caption" color="primary.main">
                                {data?.is_followed ? 'Following' : 'Follow'}
                              </Typography>
                            </div>
                            <div
                              className="flex items-center gap-2 cursor-pointer"
                              onClick={() => {
                                setShareModal(true)
                              }}
                            >
                              <NewShareIcon />
                              <Typography variant="caption" color="primary.main">
                                Share
                              </Typography>
                            </div>
                          </div> */}
                        </div>
                      </div>
                      {/* // summary part  */}
                      {data?.summary && data?.toggle && (
                        <div className="bg-white rounded-[20px] p-6">
                          <Box className="flex flex-col gap-3">
                            <Typography
                              variant="overline"
                              className="text-primary-main font-semibold tracking-wider leading-none"
                            >
                              ✦ Summary
                            </Typography>
                            <div
                              className="summary-content text-secondary-main87 text-sm"
                              dangerouslySetInnerHTML={{
                                __html: data?.summary || ''
                              }}
                            ></div>

                            <style jsx>{`
                              .summary-content :global(ul) {
                                list-style: none;
                                padding: 0;
                                margin: 0;
                                display: grid;
                                grid-template-columns: 1fr;
                                gap: 10px 24px;
                              }
                              @media (min-width: 640px) {
                                .summary-content :global(ul) {
                                  grid-template-columns: repeat(2, 1fr);
                                }
                              }
                              .summary-content :global(li) {
                                position: relative;
                                padding: 10px 14px 10px 36px;
                                line-height: 1.5;
                                font-size: 12px;
                                background: #46586b0a;
                                border-radius: 8px;
                              }
                              .summary-content :global(li)::before {
                                content: '•';
                                position: absolute;
                                left: 16px;
                                top: 6px;
                                color: #46586b;
                                font-size: 18px;
                                font-weight: 700;
                              }
                              .summary-content :global(p) {
                                margin: 0 0 8px 0;
                              }
                            `}</style>
                          </Box>
                        </div>
                      )}
                      {/* // context part  */}
                      {data?.types === 'content' &&
                        (() => {
                          const ctx = data?.context || data?.user_context || ''
                          const stripped = ctx
                            .replace(/<p>\s*(<br\s*\/?>)?\s*<\/p>/gi, '')
                            .replace(/<[^>]+>/g, '')
                            .trim()
                          if (!stripped.length) return null
                          return (
                            <div className="bg-white rounded-[20px] p-6">
                              <Box className="flex flex-col gap-3">
                                <Typography
                                  variant="overline"
                                  className="text-primary-main font-semibold tracking-wider leading-none"
                                >
                                  Context
                                </Typography>
                                <div
                                  className="context-content text-secondary-main87 text-xs whitespace-pre-line [overflow-wrap:anywhere]"
                                  dangerouslySetInnerHTML={{ __html: ctx }}
                                ></div>
                              </Box>
                            </div>
                          )
                        })()}
                      {/* // paragraph part  */}
                      {data?.paragraph && (
                        <div className="bg-white rounded-[20px] p-6">
                          <div
                            id="video-custom-size"
                            className="text-base font-normal text-secondary-main custom-anchor [overflow-wrap:anywhere]"
                            dangerouslySetInnerHTML={{
                              __html: data?.paragraph || ''
                            }}
                          ></div>
                        </div>
                      )}
                      {/* // second part  */}
                      <div className="rounded-[20px] mb-12">
                        {data?.file && (
                          <PdfViewerMerged pdfFile={data?.file} numberPosition={true} />
                        )}
                        {embedUrl && data?.types === 'video' ? (
                          <div className="relative w-full pb-[56.25%]">
                            {' '}
                            <iframe
                              className="absolute top-0 left-0 w-full h-full rounded-[20px]"
                              src={embedUrl}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            ></iframe>
                          </div>
                        ) : rawUrl ? (
                          <Box className="h-full rounded-[20px] bg-white flex flex-col md:flex-row gap-4">
                            <img
                              className="w-full md:max-w-[349px] rounded-t-[20px] md:rounded-t-none md:rounded-l-[20px]"
                              src={data?.thumbnail}
                              alt=""
                            />
                            <Box className="flex flex-col p-4 gap-2.5">
                              {data.source_publish_at && (
                                <Typography variant="caption">
                                  {'Source published date: ' +
                                    formatDate(data.source_publish_at, 'long')}
                                </Typography>
                              )}
                              <TruncatedAnswer
                                content={data?.headline}
                                textType="subtitle1"
                                maxLength={100}
                              />
                              <div className="flex gap-3">
                                {data?.source_icon && (
                                  <img
                                    className="rounded-[20px] w-[24px] h-[24px] "
                                    src={data?.source_icon}
                                    alt=""
                                  />
                                )}
                                {data?.source_name && (
                                  <Typography variant="body2" className="self-center">
                                    {data?.source_name}
                                  </Typography>
                                )}
                              </div>
                              <div className="py-1.5 ">
                                <Button
                                  onClick={() => {
                                    if (data?.content_url) {
                                      window.open(data.content_url, '_blank', 'noopener,noreferrer')
                                    }
                                  }}
                                  classes="flex gap-2"
                                  text={getWebLinkButtonText(data?.content_url)}
                                  variant="outlined"
                                  iconPosition="right"
                                  icon={<AnnouncementBoardIcon />}
                                />
                              </div>
                            </Box>
                          </Box>
                        ) : null}
                      </div>
                    </div>
                  )}
                  {/* // second  */}
                  {!hideEngagementSidebar && (
                    <div className="flex flex-col gap-4 xl:sticky xl:top-12 xl:self-start xl:max-h-[calc(100vh-100px)]">
                      {(profile.user_type === 'investor' || profile.user_type === '') && (
                        <Box className="flex flex-col gap-3 p-4 bg-white rounded-[20px] w-full xl:w-[540px] xl:self-end">
                          <div className="flex items-center gap-2">
                            <NewQuestionIcon />
                            <Typography variant="subtitle1" color="text.primary">
                              {`Ask ${questionData?.business_name ?? ''} management`}
                            </Typography>
                          </div>
                          <Typography variant="body2" color="text.secondary">
                            Questions are answered by the IR team and, where appropriate, the MD
                            directly.
                          </Typography>
                          {profile.user_type === 'investor' ? (
                            <>
                              <Box className=" bg-secondary-labelBackground rounded-[12px] px-4 py-3 flex flex-col gap-3">
                                <TextField
                                  inputRef={quarterlyFocusRef}
                                  placeholder="What would you like to know about this quarterly?"
                                  variant="standard"
                                  InputProps={{ disableUnderline: true }}
                                  multiline
                                  maxRows={4}
                                  value={question}
                                  onChange={handleChange}
                                  fullWidth
                                />
                                <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
                                  <Typography variant="caption" color="text.secondary">
                                    Answered directly to you · May also be made public anonymously
                                  </Typography>
                                  <Button
                                    text="Post"
                                    onClick={handlePost}
                                    disabled={!question.trim()}
                                  />
                                </div>
                              </Box>
                              {sendSuccess && (
                                <div>
                                  {sendSuccess === 'sent' ? (
                                    <Typography
                                      className="flex justify-center"
                                      color="text.secondary"
                                    >
                                      You have a question pending a response.
                                    </Typography>
                                  ) : (
                                    <Typography
                                      className="flex gap-2 items-center pt-2 justify-center"
                                      component="span"
                                      variant="subtitle1"
                                      color="success.main"
                                    >
                                      <SendIcon className="rotate-0 h-4 fill-success-main" />
                                      Question sent!
                                    </Typography>
                                  )}
                                  <div className="flex flex-col sm:flex-row gap-2 sm:justify-center mt-4">
                                    <Button
                                      text="Go to question"
                                      variant="text"
                                      onClick={() => {
                                        router.push('/my-questions')
                                      }}
                                    />
                                    <Button
                                      text="Submit another question"
                                      variant="contained"
                                      onClick={() => {
                                        quarterlyFocusRef.current?.focus()
                                        setSendSuccess(false)
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            profile.user_type === '' && (
                              <div className="flex justify-center pt-2">
                                <Button
                                  text="Ask question to management"
                                  onClick={() => {
                                    setRedirectionUrl(router.asPath)
                                    router.push(`/create-account`)
                                  }}
                                />
                              </div>
                            )
                          )}
                        </Box>
                      )}
                      {(profile.user_type === 'investor' || profile.user_type === '') && (
                        <Box className="flex flex-col gap-3 p-4 bg-white rounded-[20px] w-full xl:w-[540px] xl:self-end">
                          <div className="flex flex-col gap-3 lg:flex-col lg:justify-between">
                            <div className="flex flex-col">
                              <Typography variant="subtitle1" color="text.primary">
                                How did this announcement land with you?
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Anonymous · informs IR on investor sentiment
                              </Typography>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={() => handleSentiment('positive')}
                                className={`flex items-center gap-1.5 px-3 py-1 rounded-full border transition-colors ${
                                  sentiment === 'positive'
                                    ? 'border-success-main bg-success-main/10'
                                    : 'border-gray-200 hover:border-success-main'
                                }`}
                              >
                                <span>😊</span>
                                <Typography
                                  variant="caption"
                                  color="success.main"
                                  className="font-semibold"
                                >
                                  Positive
                                </Typography>
                                {/* <Typography variant="caption" color="text.secondary">
                              284
                            </Typography> */}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSentiment('neutral')}
                                className={`flex items-center gap-1.5 px-3 py-1 rounded-full border transition-colors ${
                                  sentiment === 'neutral'
                                    ? 'border-gray-400 bg-gray-100'
                                    : 'border-gray-200 hover:border-gray-400'
                                }`}
                              >
                                <span>😐</span>
                                <Typography
                                  variant="caption"
                                  color="text.primary"
                                  className="font-semibold"
                                >
                                  Neutral
                                </Typography>
                                {/* <Typography variant="caption" color="text.secondary">
                              112
                            </Typography> */}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSentiment('negative')}
                                className={`flex items-center gap-1.5 px-3 py-1 rounded-full border transition-colors ${
                                  sentiment === 'negative'
                                    ? 'border-error-red bg-error-lightRed'
                                    : 'border-gray-200 hover:border-error-red'
                                }`}
                              >
                                <span>😟</span>
                                <Typography
                                  variant="caption"
                                  color="error.main"
                                  className="font-semibold"
                                >
                                  Negative
                                </Typography>
                                {/* <Typography variant="caption" color="text.secondary">
                              37
                            </Typography> */}
                              </button>
                            </div>
                          </div>
                        </Box>
                      )}
                      <div
                        className={`shadow-none xl:shadow-lg flex flex-col bg-white xl:bg-none rounded-[20px] w-full xl:w-[540px] xl:self-end xl:flex-1 xl:min-h-0 h-[80vh] xl:h-auto mb-8`}
                      >
                        <div className="flex flex-col px-4 pt-4 pb-4 gap-4 overflow-y-auto h-full hide-scrollbar">
                          <Box className="flex justify-between items-center px-2">
                            <Box className="flex gap-2.5 items-center">
                              <NewQuestionIcon />
                              <Typography variant="h5" color="text.primary">
                                Recent questions
                              </Typography>
                            </Box>
                            {questionData?.result?.length ? (
                              <Typography variant="caption" color="text.secondary">
                                {questionData?.result?.length} on this announcement
                              </Typography>
                            ) : null}
                          </Box>
                          {questionData?.result?.length ? (
                            <div className="flex flex-col gap-4 overflow-y-auto pt-2 px-2 hide-scrollbar">
                              <div className="min-h-[calc(100vh-465px)] flex flex-col gap-6">
                                {questionData.result?.map((item, index) => {
                                  const initial =
                                    item?.user?.trim()?.charAt(0)?.toUpperCase() || '?'
                                  return (
                                    <div key={index} className="flex gap-3">
                                      <div className="w-8 h-8 rounded-full bg-primary-main flex items-center justify-center shrink-0 mt-0.5">
                                        <Typography className="text-white" variant="subtitle2">
                                          {initial}
                                        </Typography>
                                      </div>
                                      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                                        <div className="flex justify-between items-center gap-2">
                                          <Typography variant="subtitle2" color="text.primary">
                                            {item?.user}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            className="shrink-0"
                                          >
                                            {getTimeAgo(item?.created_on)}
                                          </Typography>
                                        </div>
                                        <Typography variant="body2" color="text.primary">
                                          {item?.question}
                                        </Typography>
                                        {item?.answer ? (
                                          <div className="bg-secondary-labelBackground rounded-[12px] p-3 mt-1 flex flex-col gap-2">
                                            <div className="flex items-center gap-1.5">
                                              <TickIcon className="h-3 w-3 fill-success-main" />
                                              <Typography variant="caption" color="text.secondary">
                                                Answered by {item?.answer_by ?? item?.business_name}
                                              </Typography>
                                            </div>
                                            <Typography
                                              variant="body2"
                                              color="text.primary"
                                              dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(
                                                  item?.answer?.replaceAll('\n', '<br />') ?? ''
                                                )
                                              }}
                                            />
                                          </div>
                                        ) : (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            className="italic"
                                          >
                                            Awaiting answer from management
                                          </Typography>
                                        )}
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          ) : (
                            <EmptyQuestions
                              data={questionData}
                              handleAskQuestion={handleAskQuestion}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <CustomModal
          open={shareModal}
          handleClose={() => {
            setShareModal(false)
          }}
          crossIcon={false}
        >
          <Box className="bg-white w-[37.5rem] rounded-[1.25rem]">
            <Box className="p-4 pl-6 flex justify-between items-center">
              <Typography variant="h6" color="text.primary">
                Share this announcement
              </Typography>
              <IconButton onClick={() => setShareModal(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box className="p-6 pt-4 flex flex-col gap-4">
              <Box className="flex items-center w-full">
                {/* Link container */}
                <Box className="flex items-center bg-white border border-gray-300 rounded-full overflow-hidden w-full max-h-[54px] h-full">
                  <Typography
                    color="text.secondary"
                    variant="inputValue"
                    className="px-4 py-4 truncate"
                  >
                    {fullUrl}
                  </Typography>

                  {/* Copy button */}
                  <button
                    className="bg-primary-main text-white px-4 py-4 flex items-center gap-2 hover:bg-[#0A014F]/90"
                    onClick={() => {
                      navigator?.clipboard?.writeText(SocialMediaUrl)
                      setCopied(true)
                      setTimeout(() => {
                        setCopied(false)
                      }, 1000)
                    }}
                  >
                    {copied ? (
                      <TickIcon className="fill-white h-4 w-4" />
                    ) : (
                      <>
                        <CopyToClipboardIcon className="h-4 w-4 fill-white" />
                        <Typography variant="buttonLarge_lg" className="whitespace-nowrap">
                          Copy link
                        </Typography>
                      </>
                    )}
                  </button>
                </Box>
              </Box>
              <Box className="flex gap-4">
                {/* fb */}
                <Link
                  href={`https://www.facebook.com/sharer.php?u=${encodedUrl}&quote=${encodedTitle}&picture=${encodeURIComponent(
                    data?.thumbnail ?? themeLogo?.logo ?? ''
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FacebookNewIcon />
                </Link>
                <Link
                  href={`https://twitter.com/share?text=${encodedTitle}&url=${encodedUrl}&media=${encodeURIComponent(
                    data?.thumbnail ?? themeLogo?.logo ?? ''
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <TwitterNewIcon />
                </Link>
                <Link
                  className="flex md:hidden"
                  href={`https://web.whatsapp.com/send?text=${encodedTitle}%0A${encodedDescription}%0A${encodedUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <WhatsAppIcon />
                </Link>
                <Link
                  className="hidden md:flex"
                  href={`https://web.whatsapp.com/send?text=${encodedTitle}%0A${encodedDescription}%0A${encodedUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <WhatsAppIcon />
                </Link>
                <Link
                  href={`https://www.linkedin.com/shareArticle?url=${encodedUrl}&title=${encodedTitle}&summary=${encodedDescription}&source=${encodeURIComponent(
                    data?.thumbnail ?? themeLogo?.logo ?? ''
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LinkedIn />
                </Link>
              </Box>
            </Box>
          </Box>
        </CustomModal>
      </div>
    </>
  )
}
export default AnnouncementDetail

export const getServerSideProps = async ({ req, params }: any) => {
  const slug = params?.slug
  const id = getAnnouncementIdFromSlug(slug)

  if (!id) {
    return { notFound: true }
  }

  const protocol = req.headers['x-forwarded-proto'] || 'http'
  const host = req.headers.host

  const fullUrl = `${protocol}://${host}/announcement-detail/${slug}`
  const SocialMediaUrl = fullUrl

  try {
    const cookieHeader: string = req.headers.cookie ?? ''
    // Cookie headers are ;-separated, not querystrings — pass the raw header
    // through (CommonApi stringifies it) so cookies aren't mangled/dropped.
    const serverParams = cookieHeader || null

    // Mirror the client theme call (`theme?id=<user_id>`) for logged-in users.
    const userId = cookieHeader
      .split(';')
      .map(part => part.trim())
      .find(part => part.startsWith('user_id='))
      ?.split('=')[1]

    // Backend resolves a public custom-domain tenant from Origin/Referer, which
    // the browser sends but server-side axios does not — so forward them.
    const siteOrigin = `${protocol}://${host}`
    const originHeaders = { Origin: siteOrigin, Referer: `${siteOrigin}/` }

    const [contentResp, themeResp] = await Promise.all([
      CommonApi({
        path: AnnouncementSearchEndpoint.get(id),
        method: 'get',
        serverParams,
        headers: originHeaders
      }),
      CommonApi({
        path: `theme${userId ? `?id=${userId}` : ''}`,
        method: 'get',
        serverParams,
        headers: originHeaders
      }).catch(() => null)
    ])

    const content = contentResp?.data
    const tenant = themeResp?.data
    const category = (content?.category || '').toLowerCase()
    const canonical = (tenant?.custom_site_announcements_url || '').trim().replace(/\/$/, '')

    if (tenant?.is_custom_site && canonical && category === 'announcement') {
      // Encode the same JSON string as getAnnouncementUrl (btoa(JSON.stringify(id)))
      // so the redirect's announcement= payload matches the client-generated links.
      const b64 = Buffer.from(JSON.stringify(content.id)).toString('base64')
      const sep = canonical.includes('?') ? '&' : '?'
      return {
        redirect: {
          destination: `${canonical}${sep}announcement=${encodeURIComponent(b64)}`,
          permanent: false
        }
      }
    }

    return {
      props: {
        annData: {
          respData: content
        },
        id,
        fullUrl,
        SocialMediaUrl
      }
    }
  } catch (error) {
    return { notFound: true }
  }
}
